/**
 * The Won board (BAR-2806). Each Win counts once — against the patient's first
 * PN and their market — so every row, the All row and the locations sum to the total.
 */

import { Track } from '@bariendo/lib/constants';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { QUEUE_FILTER_ANY, QUEUE_FILTER_UNASSIGNED } from './queueFilters';
import { WonLeadRow } from './wonRows';

export interface WonTrackCounts {
  selfPay: number;
  employer: number;
  insurance: number;
  total: number;
}

export interface WonPnRow extends WonTrackCounts {
  /** A Practitioner id, or `unassigned`. */
  pn: string;
  label: string;
}

export interface WonLocationCount {
  location: string;
  count: number;
}

export interface WonSummary {
  total: number;
  byPn: WonPnRow[];
  all: WonTrackCounts;
  byLocation: WonLocationCount[];
}

export const NO_LOCATION_LABEL = 'No location';

function emptyCounts(): WonTrackCounts {
  return { selfPay: 0, employer: 0, insurance: 0, total: 0 };
}

function addWin(counts: WonTrackCounts, row: WonLeadRow): void {
  if (row.wonTrack === 'employer') {
    counts.employer += 1;
  } else if (row.wonTrack === 'insurance') {
    counts.insurance += 1;
  } else {
    counts.selfPay += 1;
  }
  counts.total += 1;
}

/**
 * @param pnLabels - PN filter labels by Practitioner id, for a selected PN with no wins.
 * @param selectedPn - The PN filter's value; that PN keeps a row at zero.
 */
export function summarizeWins(rows: WonLeadRow[], pnLabels: Map<string, string>, selectedPn: string): WonSummary {
  const all = emptyCounts();
  const byPn = new Map<string, WonPnRow>();
  const byLocation = new Map<string, number>();

  const pnRow = (pn: string, label: string): WonPnRow => {
    const existing = byPn.get(pn);
    if (existing) {
      return existing;
    }
    const created = { pn, label, ...emptyCounts() };
    byPn.set(pn, created);
    return created;
  };

  for (const row of rows) {
    const navigator = row.patientNavigatorPractitioners[0];
    const label = navigator?.id
      ? (pnLabels.get(navigator.id) ?? (formatGivenFamilyName(navigator.name?.[0]) || navigator.id))
      : 'Unassigned';
    addWin(pnRow(navigator?.id ?? QUEUE_FILTER_UNASSIGNED, label), row);
    addWin(all, row);

    const location = row.location ?? NO_LOCATION_LABEL;
    byLocation.set(location, (byLocation.get(location) ?? 0) + 1);
  }

  pnRow(QUEUE_FILTER_UNASSIGNED, 'Unassigned');
  if (selectedPn !== QUEUE_FILTER_ANY && selectedPn !== QUEUE_FILTER_UNASSIGNED) {
    pnRow(selectedPn, pnLabels.get(selectedPn) ?? selectedPn);
  }

  // Busiest first; Unassigned last.
  const pnRows = [...byPn.values()].sort(
    (a, b) =>
      Number(a.pn === QUEUE_FILTER_UNASSIGNED) - Number(b.pn === QUEUE_FILTER_UNASSIGNED) ||
      b.total - a.total ||
      a.label.localeCompare(b.label)
  );

  return {
    total: all.total,
    byPn: pnRows,
    all,
    byLocation: sortedLocations(byLocation),
  };
}

function sortedLocations(byLocation: Map<string, number>): WonLocationCount[] {
  return [...byLocation.entries()]
    .map(([location, count]) => ({ location, count }))
    .sort((a, b) => b.count - a.count || a.location.localeCompare(b.location));
}

/** The Coverage pill's Won board (BAR-3099): the coverage tracks only, employer split by administrator. */
export interface WonCoverageSummary {
  total: number;
  carrum: number;
  transcarent: number;
  /** Every employer win, an unverified employer's included. */
  employer: number;
  insurance: number;
  byLocation: WonLocationCount[];
}

/** Self-pay wins are left out: this is care ops' board. The administrator is the patient's track. */
export function summarizeWinsByTrack(rows: WonLeadRow[]): WonCoverageSummary {
  const summary = { total: 0, carrum: 0, transcarent: 0, employer: 0, insurance: 0 };
  const byLocation = new Map<string, number>();

  for (const row of rows) {
    if (row.wonTrack === 'employer') {
      summary.employer += 1;
      if (row.track === Track.CARRUM) summary.carrum += 1;
      if (row.track === Track.TRANSCARENT) summary.transcarent += 1;
    } else if (row.wonTrack === 'insurance') {
      summary.insurance += 1;
    } else {
      continue;
    }
    summary.total += 1;
    const location = row.location ?? NO_LOCATION_LABEL;
    byLocation.set(location, (byLocation.get(location) ?? 0) + 1);
  }

  return { ...summary, byLocation: sortedLocations(byLocation) };
}
